/**
 * useFormSimulation - Hook pour intégrer la simulation dans FormPollCreator
 */

import { useState, useCallback, useMemo } from "react";
import { useSimulation } from "@core/hooks/useSimulation";
import type { SimulationConfig, UserTier } from "@core/types/simulation";
import { detectContext } from "@core/lib/simulation/ContextDetectionService";
import {
  getQuotaForTier,
  getRemainingSimulations,
  getRemainingToday,
  canSimulate,
  incrementUsage,
} from "@core/lib/simulation/SimulationQuotaService";
import {
  hashSimulationInput,
  getCachedResult,
} from "@core/lib/simulation/SimulationCacheService";

interface UseFormSimulationProps {
  /** Titre du poll */
  pollTitle: string;

  /** Questions du poll */
  questions: Array<{
    id: string;
    title: string;
    type: "single" | "multiple" | "text" | "long-text" | "matrix";
    required?: boolean;
    options?: Array<{ id: string; label: string }>;
    matrixRows?: Array<{ id: string; label: string }>;
    matrixColumns?: Array<{ id: string; label: string }>;
    matrixType?: "single" | "multiple";
  }>;

  /** Tier utilisateur */
  userTier: UserTier;
}

export function useFormSimulation({
  pollTitle,
  questions,
  userTier,
}: UseFormSimulationProps) {
  const [showModal, setShowModal] = useState(false);
  const [lastConfig, setLastConfig] = useState<SimulationConfig | null>(null);

  // Détection automatique du contexte
  const detectedContext = useMemo(() => {
    return detectContext(pollTitle, questions);
  }, [pollTitle, questions]);

  // Quotas
  const quota = useMemo(() => getQuotaForTier(userTier), [userTier]);
  const remainingSimulations = useMemo(
    () => getRemainingSimulations(userTier),
    [userTier],
  );
  const remainingToday = useMemo(() => getRemainingToday(userTier), [userTier]);
  const canLaunchSimulation = useMemo(() => canSimulate(userTier), [userTier]);

  // Hook simulation
  const simulation = useSimulation({
    pollId: "draft", // Sera remplacé par le vrai ID après création
    questions,
    detectedContext,
  });

  // Ouvrir le modal
  const openSimulation = useCallback(() => {
    if (canLaunchSimulation) {
      setShowModal(true);
    }
  }, [canLaunchSimulation]);

  // Lancer la simulation
  const startSimulation = useCallback(
    async (config: SimulationConfig, opts?: { force?: boolean }) => {
      setShowModal(false);
      setLastConfig(config);

      // Cache hit (questions inchangées, pas de relance forcée) → on ne
      // consomme NI quota mensuel NI appel IA : le résultat caché est resservi.
      const hash = hashSimulationInput(config, questions);
      const isCacheHit = !opts?.force && getCachedResult(hash) !== null;

      if (!isCacheHit) {
        incrementUsage(userTier);
      }

      // Lancer la simulation (sert le cache ou relance selon opts.force)
      await simulation.startSimulation(config, opts);
    },
    [userTier, simulation, questions],
  );

  // Relancer une vraie analyse (ignore le cache) — pour le bouton « Relancer »
  const rerunSimulation = useCallback(async () => {
    if (!lastConfig) return;
    incrementUsage(userTier);
    await simulation.startSimulation(lastConfig, { force: true });
  }, [lastConfig, userTier, simulation]);

  // Fermer le modal
  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return {
    // État
    showModal,
    detectedContext,
    quota,
    remainingSimulations,
    remainingToday,
    canLaunchSimulation,

    // Simulation
    ...simulation,

    // Actions
    openSimulation,
    startSimulation,
    rerunSimulation,
    closeModal,
  };
}
